import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { updateCustomer } from "../api/customersApi";

const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  phone: "",
};

function validateCustomerFields(formData) {
  const errors = {};

  if (!formData.firstName) {
    errors.firstName = "First name is required";
  } else if (!/^[a-zA-Z\s'-]{1,50}$/.test(formData.firstName)) {
    errors.firstName =
      "First name can only contain letters, spaces, hyphens, or apostrophes (max 50 characters).";
  }

  if (!formData.lastName) {
    errors.lastName = "Last name is required";
  } else if (!/^[a-zA-Z\s'-]{1,50}$/.test(formData.lastName)) {
    errors.lastName =
      "Last name can only contain letters, spaces, hyphens, or apostrophes (max 50 characters).";
  }

  if (!formData.phone) {
    errors.phone = "Phone number is required";
  } else if (!/^\d{10}$/.test(formData.phone)) {
    errors.phone = "US phone number must be 10 digits.";
  }

  return errors;
}

export default function useEditCustomer({ accessToken, userId, onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = useCallback((customer) => {
    setEditingCustomerId(customer.user_id);
    setFormData({
      firstName: customer.first_name || "",
      lastName: customer.last_name || "",
      phone: customer.phone || "",
    });
    setErrors({});
    setSubmitError("");
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData(INITIAL_FORM_DATA);
    setEditingCustomerId(null);
    setErrors({});
    setSubmitError("");
  }, []);

  const handleChange = useCallback((e) => {
    const { id, name, value } = e.target;
    const key = name || id;
    if (!key) return;
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitError("");

      const validationErrors = validateCustomerFields(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        await updateCustomer({ accessToken, userId, customerId: editingCustomerId, formData });
        toast.success("Customer updated successfully");
        closeModal();
        onSuccess?.();
      } catch (err) {
        const msg = err.message || "Failed to update customer. Please try again.";
        setSubmitError(msg);
        toast.error(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, accessToken, userId, editingCustomerId, closeModal, onSuccess],
  );

  return {
    showModal,
    openModal,
    closeModal,
    formData,
    errors,
    submitError,
    isSubmitting,
    handleChange,
    handleSubmit,
  };
}
